'use client';

import Image from 'next/image';
import Link from 'next/link';
import { Check } from 'lucide-react';
import { useWhatsApp } from '@/components/whatsapp-provider';
import { PLAY_STORE_URL, getAppHref } from '@/lib/app-links';

export function PublicFooter() {
  const { href, display } = useWhatsApp();
  return (
    <footer className="bg-[var(--sidebar-bg)] text-white/80">
      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <AppPromo />
        <div className="space-y-10 border-t border-white/10 pt-10 md:grid md:grid-cols-[1.6fr_1fr_1fr_1fr] md:gap-10 md:space-y-0 md:pt-14">
          <div>
            <Link href="/" aria-label="Frags For Less home" className="inline-flex items-center">
              <Image
                src="/frags-for-less-logo.png"
                alt="Frags For Less"
                width={1603}
                height={420}
                className="h-9 w-auto"
              />
            </Link>
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/60">
              Your wholesale partner for luxury perfumes. Niche, designer and
              Arabic fragrances, ready to stock your business.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3 md:contents">
            <FooterCol title="Get started">
              <FooterLink href="/signup">Register account</FooterLink>
              <FooterLink href="/login">Log in</FooterLink>
              {/* Third, not last: /catalog is the page every other public page
                  should be able to reach, and the footer is the only site-wide
                  link surface besides the header. Anchor text differs from the
                  header's "Catalog" and the homepage's "Browse the catalog" on
                  purpose, so the three links are not identical. */}
              <FooterLink href="/catalog">Wholesale catalog</FooterLink>
              <FooterLink href="/how-it-works">How it works</FooterLink>
              <FooterLink href="/faq">FAQ</FooterLink>
              <FooterLink href="/shipping-rates">Shipping rates</FooterLink>
              <FooterLink href="/wholesale">Wholesale supply</FooterLink>
              <FooterLink href="/brands">Brands we supply</FooterLink>
              <FooterLink href="/blog">Blog</FooterLink>
            </FooterCol>

            <FooterCol title="Company">
              <FooterLink href="/about">About us</FooterLink>
              <FooterLink href="/contact">Contact us</FooterLink>
              <FooterLink href="/legal/terms">Terms</FooterLink>
              <FooterLink href="/legal/privacy">Privacy</FooterLink>
              <FooterLink href="/legal/cookies">Cookies</FooterLink>
            </FooterCol>

            <FooterCol title="Reach us" className="col-span-2 sm:col-span-1">
              <li>
                <a
                  href={href()}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 font-display tabular text-sm text-white/65 transition-colors hover:text-white"
                >
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="size-4 shrink-0"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.04 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.002-5.45 4.436-9.884 9.889-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.886 9.884zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  {display}
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@fragsforless.com"
                  className="inline-flex items-center gap-2 text-sm text-white/65 transition-colors hover:text-white"
                >
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.8}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="size-4 shrink-0"
                  >
                    <rect x="3" y="5" width="18" height="14" rx="2" />
                    <path d="m3 7 9 6 9-6" />
                  </svg>
                  info@fragsforless.com
                </a>
              </li>
            </FooterCol>
          </div>
        </div>

        <div className="mt-12 border-t border-white/10 pt-6 text-xs text-white/50">
          <span>
            &copy; {new Date().getFullYear()}{' '}
            <Link
              href="/"
              className="text-white/65 transition-colors hover:text-white"
            >
              Frags For Less
            </Link>
            . All rights reserved.
          </span>
        </div>
      </div>
    </footer>
  );
}

const APP_POINTS = [
  'Live stock lists, updated daily',
  'Alerts the moment new stock lands',
  'Every order tracked from placed to shipped',
];

// The app, on every public page, in the two shapes the team picked (1 Oct
// 2026). On a computer: a white card with the real app screen and a QR code,
// because a computer cannot install the app but its owner's iPhone can scan
// it. On a phone or tablet: the app's real new-arrivals notification, since
// instant alerts are the one thing the website cannot do.
function AppPromo() {
  return (
    <div className="mb-10 md:mb-14">
      <AppCardDesktop />
      <AppAlertMobile />
    </div>
  );
}

function AppStoreBadge() {
  return (
    <a
      href={getAppHref('ios', 'footer')}
      target="_blank"
      rel="noopener"
      data-placement="footer"
      className="inline-flex shrink-0 rounded-[10px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
    >
      {/* Apple's official badge artwork, unmodified, as their guidelines require. */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src="/app/app-store-badge.svg"
        alt="Download on the App Store"
        width={150}
        height={50}
        className="h-12 w-auto"
      />
    </a>
  );
}

function GooglePlayBadge() {
  if (!PLAY_STORE_URL) return null;
  return (
    <a
      href={getAppHref('android', 'footer')}
      target="_blank"
      rel="noopener"
      data-placement="footer"
      className="inline-flex shrink-0 rounded-[10px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
    >
      {/* Google's official badge artwork, only its transparent margin trimmed so
          it lines up with Apple's at the same height. */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src="/app/google-play-badge.png"
        alt="Get it on Google Play"
        width={161}
        height={48}
        className="h-12 w-auto"
      />
    </a>
  );
}

function AppCardDesktop() {
  return (
    <section
      aria-labelledby="footer-app-title"
      className="hidden min-h-[400px] grid-cols-[minmax(0,1fr)_440px] overflow-hidden rounded-3xl bg-[var(--background)] text-[var(--foreground)] lg:grid"
    >
      <div className="flex flex-col justify-center py-10 pl-16 pr-10">
        <h2
          id="footer-app-title"
          className="font-display text-[40px] font-semibold leading-[1.12] tracking-[-0.02em]"
        >
          Order from your phone.
        </h2>
        <p className="mt-3.5 max-w-[480px] text-base leading-relaxed text-[var(--text-secondary)]">
          The full catalog in an app made for wholesale buyers.{' '}
          {PLAY_STORE_URL
            ? 'Free on iPhone, iPad and Android.'
            : 'Free on iPhone and iPad, Android coming soon.'}
        </p>
        <ul className="mt-5 space-y-2.5">
          {APP_POINTS.map((point) => (
            <li key={point} className="flex items-center gap-2.5 text-[15px]">
              <Check aria-hidden="true" strokeWidth={2.5} className="size-4 shrink-0 text-[var(--accent)]" />
              {point}
            </li>
          ))}
        </ul>
        <div className="mt-7 flex items-center gap-6">
          {/* Stacked where the card is narrow, side by side from xl. */}
          <div className="flex flex-col gap-2 xl:flex-row xl:gap-3">
            <AppStoreBadge />
            <GooglePlayBadge />
          </div>
          <span aria-hidden="true" className="w-px self-stretch bg-[var(--border)]" />
          <div className="flex items-center gap-3">
            {/* Encodes https://fragsforless.com/get-app, which sends an iPhone
                to the App Store and an Android phone to Google Play. */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/app/app-qr.svg"
              alt="QR code that opens the Frags For Less app in the App Store or Google Play"
              width={76}
              height={76}
              className="size-[76px] rounded-lg border border-[var(--border)] p-1"
            />
            <p className="text-[13px] leading-snug text-[var(--text-secondary)]">
              Scan with your
              <br />
              phone camera
            </p>
          </div>
        </div>
      </div>
      <div aria-hidden="true" className="relative overflow-hidden bg-[var(--slate-soft)]">
        {/* The app's Home screen (cropped from the App Store screenshots, prices
            never shown) in a plain frame that runs off the bottom of the card. */}
        <div className="absolute left-1/2 top-[52px] w-[272px] -translate-x-1/2 overflow-hidden rounded-t-[43px] border-[9px] border-b-0 border-[var(--foreground)] bg-[var(--background)] pt-4">
          <Image
            src="/app/app-home.webp"
            alt=""
            width={640}
            height={975}
            sizes="254px"
            className="w-full"
          />
        </div>
      </div>
    </section>
  );
}

function AppAlertMobile() {
  return (
    <section aria-labelledby="footer-app-title-mobile" className="lg:hidden">
      {/* The real push the app sends when new stock lands (title and body as
          written by the new-arrivals trigger). */}
      <div
        aria-hidden="true"
        className="flex h-[196px] items-center justify-center rounded-[20px] bg-white/[0.06] px-4 sm:h-[240px]"
      >
        <div className="relative w-full max-w-[400px]">
          <div className="absolute inset-x-6 -bottom-2 h-10 rounded-[20px] bg-white/30" />
          <div className="relative flex items-start gap-3 rounded-[22px] bg-[var(--background)] px-4 py-3.5 text-[var(--foreground)] shadow-[0_16px_40px_rgba(0,0,0,0.22)]">
            <Image
              src="/app/app-icon.png"
              alt=""
              width={40}
              height={40}
              className="size-10 shrink-0 rounded-[10px]"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-3">
                <p className="text-[15px] font-semibold leading-snug">New arrivals just landed</p>
                <span className="text-[13px] text-[var(--foreground)]/50">now</span>
              </div>
              <p className="mt-0.5 text-sm leading-snug">
                Fresh stock just arrived in Stock Lot 15. Tap to browse the newest bottles.
              </p>
            </div>
          </div>
        </div>
      </div>
      <h2
        id="footer-app-title-mobile"
        className="font-display mt-6 text-[28px] font-semibold leading-[1.15] tracking-[-0.015em] text-white"
      >
        Be first to new stock.
      </h2>
      <p className="mt-2.5 max-w-md text-[15px] leading-relaxed text-white/70">
        Allow notifications in the Frags For Less app and hear about new arrivals the moment
        they go live.
      </p>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <AppStoreBadge />
        <GooglePlayBadge />
        {!PLAY_STORE_URL && <span className="text-[13px] text-white/60">Android coming soon</span>}
      </div>
    </section>
  );
}

function FooterCol({
  title,
  children,
  className,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <h4 className="font-display text-sm font-semibold text-white">{title}</h4>
      <ul className="mt-4 space-y-2.5 text-sm">{children}</ul>
    </div>
  );
}

function FooterLink({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <li>
      <Link href={href} className="text-white/65 transition-colors hover:text-white">
        {children}
      </Link>
    </li>
  );
}
